import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

/**
 * GlobalVariablesContext
 * 대시보드 차트 간 상호작용을 위한 중앙 변수 pool 관리
 * 
 * 주요 기능:
 * 1. 차트 클릭 이벤트로 변수 publish
 * 2. 변수 구독 차트 자동 업데이트
 * 3. 기본값 관리 및 변수 생명주기 관리
 */

const GlobalVariablesContext = createContext();

export const useGlobalVariables = () => {
  const context = useContext(GlobalVariablesContext);
  if (!context) {
    throw new Error('useGlobalVariables must be used within GlobalVariablesProvider');
  }
  return context;
};

export const GlobalVariablesProvider = ({ children }) => {
  // 중앙 변수 pool
  const [variables, setVariables] = useState({});
  
  // 변수 발행자 추적 (어떤 컴포넌트가 어떤 변수를 발행했는지)
  // { variableName: componentId }
  const variableSourcesRef = useRef({});
  
  // 구독 관계 추적 (어떤 컴포넌트가 어떤 변수를 구독하는지)
  const subscriptionsRef = useRef({});
  
  // 변수 변경 리스너
  const listenersRef = useRef({});

  // 컴포넌트별 배치 상태 추적
  const pendingUpdatesRef = useRef({});
  
  // 업데이트 중인 컴포넌트 추적
  const [updatingComponents, setUpdatingComponents] = useState(new Set());

  // 전역 업데이트 모드 (변수 구독 컴포넌트 업데이트 시)
  const [globalUpdateMode, setGlobalUpdateMode] = useState(false);

  // 로딩 중인 컴포넌트 추적 (실제 데이터 로딩 상태)
  const [loadingComponents, setLoadingComponents] = useState(new Set());
  const loadingCountsRef = useRef({});
  
  // 요청 기반 오버레이: in-flight 요청 추적
  const [inFlightCount, setInFlightCount] = useState(0);
  const inFlightTokensRef = useRef(new Map()); // token -> { componentId, timeoutId }
  const inFlightByComponentRef = useRef(new Map()); // componentId -> count
  
  // 디버그 모드 (localStorage 동기화)
  const [debugMode, setDebugModeState] = useState(() => {
    try {
      const stored = localStorage.getItem('enerwiser.debug.globalVariables');
      return stored === 'true';
    } catch (e) {
      return false;
    }
  });

  // publish 후 완료 누락 대비 타임아웃 관리
  const timeoutsRef = useRef({});

  // 디버그 모드 setter: 상태 + localStorage 동기화
  const setDebugMode = useCallback((value) => {
    setDebugModeState(value);
    try {
      localStorage.setItem('enerwiser.debug.globalVariables', value ? 'true' : 'false');
    } catch (e) {
      // noop
    }
  }, []);

  /**
   * 요청 시작(토큰 발급) - 요청 기반 오버레이 소스오브트루스
   * 개별 요청 단위로 토큰을 발급하고, 10초 타임아웃으로 자동 종료를 보장합니다.
   */
  const beginRequest = useCallback((componentId) => {
    try {
      const token = `${componentId || 'unknown'}_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      // in-flight 전체 카운트 증가
      setInFlightCount((prev) => prev + 1);
      // 컴포넌트별 카운트 증가
      const current = inFlightByComponentRef.current.get(componentId) || 0;
      inFlightByComponentRef.current.set(componentId, current + 1);
      // 10초 타임아웃으로 자동 종료
      const timeoutId = setTimeout(() => {
        try {
          // 타임아웃으로 종료
          if (inFlightTokensRef.current.has(token)) {
            inFlightTokensRef.current.delete(token);
            setInFlightCount((prev) => Math.max(0, prev - 1));
            const countNow = (inFlightByComponentRef.current.get(componentId) || 1) - 1;
            if (countNow > 0) {
              inFlightByComponentRef.current.set(componentId, countNow);
            } else {
              inFlightByComponentRef.current.delete(componentId);
            }
          }
        } catch (_) {}
      }, 10000);
      inFlightTokensRef.current.set(token, { componentId, timeoutId });
      return token;
    } catch (_) {
      return null;
    }
  }, []);

  /**
   * 요청 종료(토큰 반환)
   */
  const endRequest = useCallback((token) => {
    if (!token) return;
    const meta = inFlightTokensRef.current.get(token);
    if (!meta) return;
    try {
      const { componentId, timeoutId } = meta;
      try { clearTimeout(timeoutId); } catch (_) {}
      inFlightTokensRef.current.delete(token);
      setInFlightCount((prev) => Math.max(0, prev - 1));
      const countNow = (inFlightByComponentRef.current.get(componentId) || 1) - 1;
      if (countNow > 0) {
        inFlightByComponentRef.current.set(componentId, countNow);
      } else {
        inFlightByComponentRef.current.delete(componentId);
      }
    } catch (_) {}
  }, []);

  // storage 이벤트로 다른 탭/페이지 변경 사항 반영
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'enerwiser.debug.globalVariables') {
        setDebugModeState(e.newValue === 'true');
      }
    };
    window.addEventListener('storage', handleStorage);
    // 동일 탭 내 커스텀 이벤트 수신 (관리자 페이지 토글 시 즉시 반영)
    const handleCustom = (e) => {
      if (typeof e.detail === 'boolean') {
        setDebugModeState(e.detail);
      }
    };
    window.addEventListener('enerwiser:debug-globalVariables', handleCustom);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('enerwiser:debug-globalVariables', handleCustom);
    };
  }, []);

  /**
   * 변수 publish
   * @param {Object} updates - 업데이트할 변수들 { key: value }
   * @param {string} sourceId - publish한 컴포넌트 ID (디버그용)
  */
  // variables의 ref 추가 (최신 값 참조용)
  const variablesRef = useRef({});
  variablesRef.current = variables;

  /**
   * 컴포넌트 로딩 완료 알림
   * @param {string} componentId - 로딩이 완료된 컴포넌트 ID
   */
  const notifyLoadingComplete = useCallback((componentId) => {
    const counts = loadingCountsRef.current;
    if (counts[componentId]) {
      counts[componentId] -= 1;
      if (counts[componentId] <= 0) {
        delete counts[componentId];
      }
    }

    if (timeoutsRef.current[componentId]) {
      try { clearTimeout(timeoutsRef.current[componentId]); } catch (_) {}
      delete timeoutsRef.current[componentId];
    }

    const nextLoadingSet = new Set(
      Object.entries(counts)
        .filter(([, count]) => count > 0)
        .map(([componentIdKey]) => componentIdKey)
    );

    if (nextLoadingSet.size === 0) {
      setGlobalUpdateMode(false);
      setUpdatingComponents(new Set());
    }

    // 디버그 로그 제거

    setLoadingComponents(nextLoadingSet);
  }, [debugMode]);
  
  const publishVariables = useCallback((updates, sourceId = 'unknown') => {
    if (!updates || typeof updates !== 'object') {
      console.warn('[GlobalVariables] Invalid updates object:', updates);
      return;
    }

    // 디버그 로그 제거

    // 발행된 변수의 출처를 기록
    Object.keys(updates).forEach(varName => {
      variableSourcesRef.current[varName] = sourceId;
    });

    // 영향받는 컴포넌트 계산
    const affectedComponentIds = new Set();
    Object.keys(updates).forEach(varName => {
      // 해당 변수를 구독하는 모든 컴포넌트 찾기
      Object.entries(subscriptionsRef.current).forEach(([componentId, subscribedVars]) => {
        if (subscribedVars && subscribedVars[varName]) {
          affectedComponentIds.add(componentId);
        }
      });
    });

    // 1개 이상 컴포넌트가 영향받으면 전역 업데이트 모드
    if (affectedComponentIds.size >= 1) {
      setGlobalUpdateMode(true);

      affectedComponentIds.forEach(id => {
        loadingCountsRef.current[id] = (loadingCountsRef.current[id] || 0) + 1;

        if (!timeoutsRef.current[id]) {
          timeoutsRef.current[id] = setTimeout(() => {
            if (debugMode) {
              console.warn(`[GlobalVariables] Timeout fallback fired for ${id}`);
            }
            notifyLoadingComplete(id);
          }, 10000);
        }
      });

      setLoadingComponents(new Set(
        Object.entries(loadingCountsRef.current)
          .filter(([, count]) => count > 0)
          .map(([componentId]) => componentId)
      ));
    }

    // 개별 컴포넌트 업데이트 추적
    if (affectedComponentIds.size > 0) {
      setUpdatingComponents(new Set(affectedComponentIds));
    }

    setVariables(prev => {
      const newVars = { ...prev, ...updates };
      
      // 변경된 변수 목록
      const changedKeys = Object.keys(updates).filter(
        key => prev[key] !== updates[key]
      );
      
      if (changedKeys.length > 0) {
        // 디버그 로그 제거
        
        // 변경된 변수를 구독하는 리스너들에게 알림 (배치)
        changedKeys.forEach(key => {
          if (listenersRef.current[key]) {
            listenersRef.current[key].forEach(listener => {
              listener({
                varName: key,
                newValue: updates[key],
                oldValue: prev[key]
              });
            });
          }
        });
      }
      
      return newVars;
    });
  }, [debugMode, notifyLoadingComplete]);

  /**
   * 변수 구독
   * @param {string} componentId - 구독하는 컴포넌트 ID
   * @param {Object} subscribedVars - 구독할 변수와 기본값 { varName: { default: value } }
   * @param {Function} onUpdate - 변수 변경 시 콜백
   */
  const subscribeToVariables = useCallback((componentId, subscribedVars, onUpdate) => {
    if (!subscribedVars || typeof subscribedVars !== 'object') {
      return () => {}; // cleanup 함수 반환
    }

    // 구독 관계 등록
    subscriptionsRef.current[componentId] = subscribedVars;

    if (!pendingUpdatesRef.current[componentId]) {
      pendingUpdatesRef.current[componentId] = {
        scheduled: false,
        pending: {}
      };
    }
    const batchState = pendingUpdatesRef.current[componentId];

    const flushPending = () => {
      batchState.scheduled = false;
      const updates = batchState.pending;
      batchState.pending = {};
      const keys = Object.keys(updates);
      if (keys.length > 0 && typeof onUpdate === 'function') {
        onUpdate(updates);
      }
    };

    const scheduleFlush = () => {
      if (batchState.scheduled) {
        return;
      }
      batchState.scheduled = true;
      Promise.resolve().then(() => {
        flushPending();
      });
    };

    // 리스너들을 저장할 배열
    const listeners = [];

    // 각 변수에 대한 리스너 등록
    Object.keys(subscribedVars).forEach(varName => {
      if (!listenersRef.current[varName]) {
        listenersRef.current[varName] = [];
      }
      
      const listener = ({ varName: changedVar, newValue, oldValue }) => {
        // 디버그 로그 제거
        batchState.pending[changedVar] = { newValue, oldValue };
        scheduleFlush();
      };
      
      listenersRef.current[varName].push(listener);
      listeners.push({ varName, listener }); // cleanup을 위해 저장
    });
    
    // cleanup 함수
    return () => {
      delete subscriptionsRef.current[componentId];
      delete pendingUpdatesRef.current[componentId];
      
      // 저장된 리스너들 제거
      listeners.forEach(({ varName, listener }) => {
        if (listenersRef.current[varName]) {
          listenersRef.current[varName] = listenersRef.current[varName].filter(
            l => l !== listener
          );
        }
      });
    };
  }, [debugMode]);

  /**
   * 현재 변수 값 가져오기 (기본값 포함)
   * @param {string} varName - 변수명
   * @param {*} defaultValue - 기본값
   */
  const getVariable = useCallback((varName, defaultValue = null) => {
    return variables[varName] !== undefined ? variables[varName] : defaultValue;
  }, [variables]);

  /**
   * 여러 변수를 한번에 가져오기
   * @param {Object} varDefinitions - { varName: { default: value } }
   */
  const getVariables = useCallback((varDefinitions) => {
    const result = {};
    
    Object.entries(varDefinitions).forEach(([varName, config]) => {
      const defaultValue = config?.default ?? null;
      result[varName] = getVariable(varName, defaultValue);
    });
    
    return result;
  }, [getVariable]);

  /**
   * 변수 초기화
   */
  const clearVariables = useCallback(() => {
    setVariables({});
    // 디버그 로그 제거
  }, [debugMode]);

  /**
   * 특정 변수 제거
   */
  const removeVariable = useCallback((varName) => {
    setVariables(prev => {
      const { [varName]: removed, ...rest } = prev;
      // 발행자 정보도 제거
      delete variableSourcesRef.current[varName];
      return rest;
    });
  }, []);

  /**
   * 특정 컴포넌트가 발행한 모든 변수 제거
   * @param {string} componentId - 컴포넌트 ID
   */
  const removeVariablesBySource = useCallback((componentId) => {
    if (!componentId) return;
    
    // 해당 컴포넌트가 발행한 변수명들 찾기
    const varsToRemove = Object.entries(variableSourcesRef.current)
      .filter(([varName, sourceId]) => sourceId === componentId)
      .map(([varName]) => varName);
    
    if (varsToRemove.length === 0) return;
    
    // 영향받는 컴포넌트 계산 (삭제되는 변수를 구독하는 컴포넌트들)
    const affectedComponentIds = new Set();
    varsToRemove.forEach(varName => {
      // 해당 변수를 구독하는 모든 컴포넌트 찾기
      Object.entries(subscriptionsRef.current).forEach(([compId, subscribedVars]) => {
        if (subscribedVars && subscribedVars[varName]) {
          affectedComponentIds.add(compId);
        }
      });
    });

    // 1개 이상 컴포넌트가 영향받으면 전역 업데이트 모드
    if (affectedComponentIds.size >= 1) {
      setGlobalUpdateMode(true);

      affectedComponentIds.forEach(id => {
        loadingCountsRef.current[id] = (loadingCountsRef.current[id] || 0) + 1;

        if (!timeoutsRef.current[id]) {
          timeoutsRef.current[id] = setTimeout(() => {
            if (debugMode) {
              console.warn(`[GlobalVariables] Timeout fallback fired for ${id}`);
            }
            notifyLoadingComplete(id);
          }, 10000);
        }
      });

      setLoadingComponents(new Set(
        Object.entries(loadingCountsRef.current)
          .filter(([, count]) => count > 0)
          .map(([compId]) => compId)
      ));
    }

    // 개별 컴포넌트 업데이트 추적
    if (affectedComponentIds.size > 0) {
      setUpdatingComponents(new Set(affectedComponentIds));
    }
    
    // 변수들 제거
    setVariables(prev => {
      const newVars = { ...prev };
      varsToRemove.forEach(varName => {
        delete newVars[varName];
        delete variableSourcesRef.current[varName];
      });
      
      // 삭제된 변수를 구독하는 리스너들에게 알림 (변수가 삭제되었음을 통지)
      varsToRemove.forEach(varName => {
        if (listenersRef.current[varName]) {
          listenersRef.current[varName].forEach(listener => {
            listener({
              varName: varName,
              newValue: undefined, // 삭제되었으므로 undefined
              oldValue: prev[varName]
            });
          });
        }
      });
      
      return newVars;
    });
    
    // 디버그 로그
    if (debugMode) {
      try {
        console.log('[GlobalVariables] Removed variables by source:', {
          componentId,
          removedVars: varsToRemove,
          affectedComponents: Array.from(affectedComponentIds)
        });
      } catch (_) {}
    }
  }, [debugMode, notifyLoadingComplete]);

  /**
   * 구독 정보 가져오기 (디버그용)
   */
  const getSubscriptions = useCallback(() => {
    return subscriptionsRef.current;
  }, []);

  /**
   * 변수 표현식 평가
   * @param {string} expression - "$event.col1 + 10" 같은 표현식
   * @param {Object} context - { $event: {...} } 같은 컨텍스트
   */
  // 언마운트 시 잔여 타이머 정리
  useEffect(() => {
    return () => {
      Object.values(timeoutsRef.current).forEach(t => {
        try { clearTimeout(t); } catch (_) {}
      });
      timeoutsRef.current = {};
    };
  }, []);

  const evaluateExpression = useCallback((expression, context = {}) => {
    if (typeof expression !== 'string') {
      return expression;
    }

    // 단순 변수 참조 ($event.xxx) - 연산자가 없는 경우만
    if (expression.startsWith('$') && !expression.includes('*') && !expression.includes('+') && 
        !expression.includes('-') && !expression.includes('/') && !expression.includes('?')) {
      // $event.col1 → ['$event', 'col1']로 분리
      const fullPath = expression.split('.');
      const varName = fullPath[0]; // '$event'
      const properties = fullPath.slice(1); // ['col1']
      
      // context에서 변수 찾기
      let value = context[varName];
      
      // 나머지 속성들 접근
      for (const prop of properties) {
        if (value && typeof value === 'object') {
          value = value[prop];
        } else {
          // 디버그 로그 제거
          return null;
        }
      }
      
      return value;
    }
    
    // 조건식이나 계산식 평가 (보안 주의)
    try {
      // 안전한 평가를 위해 Function 생성자 사용
      const func = new Function('$event', 'context', `
        // $event는 이미 매개변수로 전달됨
        return ${expression};
      `);
      return func(context.$event, context);
    } catch (error) {
      console.warn('[GlobalVariables] Expression evaluation failed:', expression, error);
      return expression; // 평가 실패 시 원본 반환
    }
  }, []);

  const value = {
    variables,
    publishVariables,
    subscribeToVariables,
    getVariable,
    getVariables,
    clearVariables,
    removeVariable,
    removeVariablesBySource,
    evaluateExpression,
    getSubscriptions,
    debugMode,
    setDebugMode,
    updatingComponents,
    globalUpdateMode,
    loadingComponents,
    notifyLoadingComplete,
    // 요청 기반 오버레이용 API/플래그
    beginRequest,
    endRequest,
    requestOverlayActive: inFlightCount > 0,
    isComponentUpdating: useCallback((componentId) => {
      return updatingComponents.has(componentId);
    }, [updatingComponents]),
    isComponentLoading: useCallback((componentId) => {
      return loadingComponents.has(componentId);
    }, [loadingComponents])
  };

  return (
    <GlobalVariablesContext.Provider value={value}>
      {children}
    </GlobalVariablesContext.Provider>
  );
};

export default GlobalVariablesContext;
